import {
  Component,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import {
  combineLatest,
  filter,
  map,
  startWith,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { UserProfile } from '../../core/interfaces';
import { ProfileService } from '../../core/profile.service';
import { LayoutService } from '../../core/layout.service';
import { OVERLAY_ANCHOR_ELEMENT } from '../../core/tokens/anchor.token';
import { UserDropdown } from '../../nav/user-dropdown/user-dropdown';
import { FlyoutAnchorDirective } from '../../shared/ui/flyout/directives/flyout-anchor.directive';
import { ToolbarBridgeService } from '../../infrastructure/ui/services/toolbar-bridge.service';
import { NavItem, NavLogoIcon } from '../../shared/ui/nav-menu/nav-menu.model';
import { NavMenu } from '../../shared/ui/nav-menu/nav-menu';
import { TpToolbar } from '../../shared/ui/tp-toolbar/tp-toolbar';
import { NavigationService } from '../../core/navigation/navigation.service';

@Component({
  selector: 'app-standard-layout',
  imports: [
    RouterOutlet,
    MatToolbarModule,
    CommonModule,
    UserDropdown,
    FlyoutAnchorDirective,
    NavMenu,
    TpToolbar,
  ],
  templateUrl: './standard-layout.component.html',
  styleUrl: './standard-layout.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: OVERLAY_ANCHOR_ELEMENT,
      useExisting: ElementRef,
    },
  ],
})
export class StandardLayoutComponent implements OnInit {
  layoutService: LayoutService = inject(LayoutService);
  profileService: ProfileService = inject(ProfileService);
  private routerEventSubscription: Subscription | undefined;
  bridge = inject(ToolbarBridgeService);
  navigation = inject(NavigationService);

  title = '';
  loadApplication = false;
  userprofile!: UserProfile;

  isCollapsed = false;

  layoutClass = signal<string>('');
  readonly applicationName = 'Tull 4';
  readonly applicationIcon: NavLogoIcon = {
    icon: '/svg/logo.svg',
    iconType: 'vector',
  };
  readonly navItems = this.navigation.menuItems;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {
    effect(() => {
      this.title = this.layoutService.mainPageTitle();
    });
  }

  ngOnInit() {
    this.routerEventSubscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        map(() => {
          let route = this.activatedRoute.root;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        switchMap((leafRoute) => {
          const path = leafRoute.pathFromRoot;

          return combineLatest(path.map((r) => r.data)).pipe(
            take(1),
            map((dataArray) => ({ dataArray, leafRoute })),
          );
        }),
      )
      .subscribe(({ dataArray, leafRoute }) => {
        let title = '';
        let layoutClass = '';

        dataArray.forEach((data) => {
          if (data['layoutClass']) {
            layoutClass = data['layoutClass'];
          }

          if (data['resolvedData']?.pageTitle) {
            title = data['resolvedData'].pageTitle;
          }
        });

        this.layoutService.setPageTitle(
          title || leafRoute.snapshot.title || '',
        );
        this.layoutClass.set(layoutClass);
      });
  }
}
